// src/components/discover/MatchesGrid.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import {
  StarRounded,
  CloseOutlined,
  ChatOutlined,
  DirectionsOutlined,
  DeleteOutlineOutlined,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import { Match, useMatches } from '../../hooks/useMatches';
import { useIsPinned, usePinActions } from '../../hooks/usePinned';
import { useT } from '../../i18n/useT';

export const MatchesGrid: React.FC<{
  onOpenChat: (chatPrompt: string) => void;
}> = ({ onOpenChat }) => {
  const t = useT();
  const { matches, isLoading, unmatch } = useMatches();
  const { pin, unpin } = usePinActions();
  const [detailMatch, setDetailMatch] = useState<Match | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t("Loading matches…")}</Typography>
      </Box>
    );
  }

  if (matches.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 48, mb: 1 }}>💚</Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          
          {t("No matches yet")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 320, mx: 'auto' }}
        >
          
          {t("Swipe right on cards in the Discover feed to save places you like. They'll show up here.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {matches.length} {matches.length === 1 ? 'match' : 'matches'}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 1.5,
        }}
      >
        {matches.map((m) => (
          <Card
            key={m.id}
            variant="outlined"
            onClick={() => setDetailMatch(m)}
            sx={{
              borderRadius: 2,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            {m.super && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  bgcolor: '#60a5fa',
                  color: 'white',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <StarRounded sx={{ fontSize: 14 }} />
              </Box>
            )}
            {m.image_url ? (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4/3',
                  backgroundImage: `url(${m.image_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  bgcolor: 'action.hover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4/3',
                  background:
                    'linear-gradient(135deg, #f5f3f0 0%, #e6dfd3 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: 32, opacity: 0.4 }}>📍</Typography>
              </Box>
            )}
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}
                noWrap
              >
                {m.title}
              </Typography>
              {m.tags.slice(0, 2).map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', mr: 0.5, mb: 0.25 }}
                />
              ))}
              {m.matched_at && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {formatDate(m.matched_at)}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Detail sheet */}
      <Dialog
        open={!!detailMatch}
        onClose={() => setDetailMatch(null)}
        maxWidth="xs"
        fullWidth
      >
        {detailMatch && (
          <>
            <DialogContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Typography variant="h6" sx={{ flex: 1, lineHeight: 1.2 }}>
                  {detailMatch.title}
                </Typography>
                {detailMatch.super && (
                  <Chip
                    icon={<StarRounded sx={{ fontSize: 16 }} />}
                    label={t("Super")}
                    size="small"
                    sx={{ bgcolor: '#60a5fa22', color: '#60a5fa' }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => setDetailMatch(null)}
                  sx={{ ml: 1 }}
                >
                  <CloseOutlined />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {detailMatch.description}
              </Typography>
              {detailMatch.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {detailMatch.tags.map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
              {detailMatch.matched_at && (
                <Typography variant="caption" color="text.secondary">
                  
                  {t("Matched")} {formatDate(detailMatch.matched_at)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<ChatOutlined />}
                onClick={() => {
                  onOpenChat(detailMatch.chat_prompt);
                  setDetailMatch(null);
                }}
                sx={{ flex: 1, minWidth: 120 }}
              >
                
                {t("Ask")}
              </Button>
              {(() => {
                // Maps URL: prefer place_id when available, fall back to
                // name + coordinates so knowledge picks (no place_id) still
                // get a working button.
                const hasCoords =
                  typeof detailMatch.location?.lat === 'number' &&
                  typeof detailMatch.location?.lng === 'number' &&
                  !(
                    detailMatch.location.lat === 0 &&
                    detailMatch.location.lng === 0
                  );
                let url: string | null = null;
                if (detailMatch.place_id) {
                  url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    detailMatch.title || ''
                  )}&query_place_id=${encodeURIComponent(
                    detailMatch.place_id
                  )}`;
                } else if (detailMatch.title && hasCoords) {
                  url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    detailMatch.title
                  )}&center=${detailMatch.location.lat},${detailMatch.location.lng}`;
                } else if (hasCoords) {
                  url = `https://www.google.com/maps/search/?api=1&query=${detailMatch.location.lat},${detailMatch.location.lng}`;
                }
                if (!url) return null;
                return (
                  <Tooltip title={t("Open in Maps")}>
                    <IconButton
                      onClick={() => window.open(url!, '_blank', 'noopener')}
                    >
                      <DirectionsOutlined />
                    </IconButton>
                  </Tooltip>
                );
              })()}
              <PinButtonForMatch
                match={detailMatch}
                pin={pin}
                unpin={unpin}
              />
              <Tooltip title={t("Unmatch")}>
                <IconButton
                  onClick={() => {
                    unmatch(detailMatch.id);
                    setDetailMatch(null);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteOutlineOutlined />
                </IconButton>
              </Tooltip>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

/** Subscribes to the pinned-doc state for a match and renders the
 *  toggle button. Standalone component so the subscription only
 *  exists when the dialog is open and a match is selected. */
const PinButtonForMatch: React.FC<{
  match: Match;
  pin: ReturnType<typeof usePinActions>['pin'];
  unpin: ReturnType<typeof usePinActions>['unpin'];
}> = ({ match, pin, unpin }) => {
  const isPinned = useIsPinned(match.id);
  const hasCoords =
    typeof match.location?.lat === 'number' &&
    typeof match.location?.lng === 'number' &&
    !(match.location.lat === 0 && match.location.lng === 0);
  if (!hasCoords) return null;
  return (
    <Tooltip title={isPinned ? 'Unpin from map' : 'Pin to map'}>
      <IconButton
        onClick={() => {
          if (isPinned) {
            unpin(match.id);
          } else {
            pin({
              id: match.id,
              title: match.title,
              description: match.description,
              image_url: match.image_url ?? null,
              location: match.location,
              place_id: match.place_id,
              chat_prompt: match.chat_prompt,
              source: 'match',
            });
          }
        }}
        sx={{
          color: isPinned ? 'warning.main' : 'text.secondary',
        }}
      >
        {isPinned ? <PushPin /> : <PushPinOutlined />}
      </IconButton>
    </Tooltip>
  );
};

function formatDate(d: Date): string {
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 30 * day) return `${Math.round(diff / day)} days ago`;
  return d.toLocaleDateString();
}
